// WHAT: Form component for entering/editing OAuth Client Credentials
// WHY:  Separates form logic from main component to reduce complexity
// DOMAIN: Settings → Yazio
// DEPENDENCIES: React, @/components/ui, lucide-react

import { CheckCircle, Info, Key, Loader2, Shield, X } from "lucide-react";
import React from "react";

import { Button, Card, Heading, Input, Label, Stack } from "@/components/ui";

interface OAuthClientCredentialsFormProperties {
  /** Client ID input value */
  clientId: string;
  /** Client Secret input value */
  clientSecret: string;
  /** Whether OAuth credentials are already configured */
  hasClientCredentials: boolean;
  /** Whether form is in loading state */
  loading: boolean;
  /** Handler for cancel action */
  onCancel: () => void;
  /** Handler for client ID change */
  onClientIdChange: (value: string) => void;
  /** Handler for client secret change */
  onClientSecretChange: (value: string) => void;
  /** Handler for save action */
  onSave: () => void;
  /** Whether form is in saving state */
  saving: boolean;
}

/**
 * WHAT:
 *   Form component for entering/editing OAuth Client Credentials.
 *
 * WHY:
 *   Separates form UI from main component to reduce complexity.
 *
 * @param root0
 *
 * @param root0.clientId
 *
 * @param root0.clientSecret
 *
 * @param root0.loading
 *
 * @param root0.hasClientCredentials
 *
 * @param root0.saving
 *
 * @param root0.onCancel
 *
 * @param root0.onClientIdChange
 *
 * @param root0.onClientSecretChange
 *
 * @param root0.onSave
 *
 * @returns {JSX.Element} OAuth Client Credentials form component
 *
 */
export const OAuthClientCredentialsForm: React.FC<OAuthClientCredentialsFormProperties> = ({
  clientId,
  clientSecret,
  hasClientCredentials,
  loading,
  onCancel,
  onClientIdChange,
  onClientSecretChange,
  onSave,
  saving,
}) => {
  return (
    <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
      <Stack direction="col" gap={6}>
        {/* Header */}
        <Stack align="center" direction="row" gap={3}>
          <div className="p-2 bg-purple-500/10 rounded-lg">
            <Shield className="w-5 h-5 text-purple-500" />
          </div>
          <Heading className="text-lg font-medium text-white" level={3}>
            {hasClientCredentials
              ? "Update OAuth Client Credentials"
              : "Configure OAuth Client Credentials"}
          </Heading>
        </Stack>

        <Card className="bg-blue-500/10 border-blue-500/20" padding="md" variant="default">
          <Stack align="start" direction="row" gap={3}>
            <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-300">
                OAuth Client Credentials are required for API authentication. These should be
                configured once and are stored securely in the keyring. The Client Secret is highly
                sensitive and will never be displayed.
              </p>
            </div>
          </Stack>
        </Card>

        {/* Client ID Field */}
        <Stack direction="col" gap={2}>
          <Label className="text-slate-300 flex items-center gap-2">
            <Key className="w-4 h-4" />
            <span>Client ID</span>
            <span className="text-red-400">*</span>
          </Label>
          <Input
            className="w-full"
            disabled={loading || saving}
            onChange={(e) => {
              onClientIdChange(e.target.value);
            }}
            placeholder="Enter Yazio OAuth Client ID..."
            size="md"
            type="text"
            value={clientId}
          />
        </Stack>

        {/* Client Secret Field */}
        <Stack direction="col" gap={2}>
          <Label className="text-slate-300 flex items-center gap-2">
            <Key className="w-4 h-4" />
            <span>Client Secret</span>
            <span className="text-red-400">*</span>
          </Label>
          <Input
            className="w-full"
            disabled={loading || saving}
            onChange={(e) => {
              onClientSecretChange(e.target.value);
            }}
            placeholder="Enter Yazio OAuth Client Secret..."
            size="md"
            type="password"
            value={clientSecret}
          />
        </Stack>

        {/* Action Buttons */}
        <Stack className="flex-wrap" direction="row" gap={2}>
          <Button
            disabled={loading || saving || !clientId.trim() || !clientSecret.trim()}
            onClick={() => {
              onSave();
            }}
            size="md"
            variant="primary"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                {hasClientCredentials ? "Update Client Credentials" : "Save Client Credentials"}
              </>
            )}
          </Button>

          {hasClientCredentials ? (
            <Button disabled={saving} onClick={onCancel} size="md" variant="ghost">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Card>
  );
};

